import * as THREE from 'three';
import { hexToWorld, LITTER_BACK, MIRROR_MARK, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** The stretcher: two poles, the canvas, a lantern on its pole. */
const LITTER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 10.5h13M1.5 12.5h13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M4 10.5c1.5-2 6.5-2 8 0" fill="currentColor"/><path d="M12.5 10.5V3.5h-2" stroke="currentColor" stroke-width="1.1" fill="none"/><rect x="9.6" y="3.6" width="1.8" height="2.4" rx=".5" fill="currentColor"/></svg>';
/** A heal reaching her: a small cross in a ring. */
const MEND = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 4.8v6.4M4.8 8h6.4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
/** A limpet: a ribbed flat shell. */
const LIMPET = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 11.5c1-4.5 3.5-7 6-7s5 2.5 6 7z" fill="currentColor"/><path d="M8 5v6.3M5.2 6.6 6.4 11.4M10.8 6.6 9.6 11.4" stroke="#0c0e12" stroke-width=".9"/><path d="M1.5 12.5h13" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** The lantern. */
const LANTERN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3.5h4M8 1.5v2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><rect x="5" y="4" width="6" height="8" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 6.2c1 1.2 1 2.6 0 3.6-1-1-1-2.4 0-3.6z" fill="currentColor"/><path d="M4.5 13.5h7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** The lamp over the door, its rays. */
const LAMP = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 14V7.5a4 4 0 0 1 8 0V14" fill="none" stroke="currentColor" stroke-width="1.3"/><circle cx="8" cy="5" r="1.8" fill="currentColor"/><path d="M8 .8v1.4M3.6 2.6l1 1M12.4 2.6l-1 1" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>';
/** A mirror on its stand. */
const MIRROR = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="6.5" rx="4.6" ry="4.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M5.6 5.2l3-2.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M8 11.1V14.5M5.5 14.5h5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** A hook. */
const HOOK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 14.5V2.5h5.5v2.2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M10.5 4.7c-1.6 0-2.4 1.4-1.4 2.6" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';

type Mark = { el: HTMLButtonElement; at: number; point: THREE.Vector3; ico: HTMLElement };

/**
 * The cleric's last road on screen (docs/sendas/clerigo.md, chapter 1), inside a path's own plate (game/senda-hud.ts)
 * and over the board; a phone's plate kept under about a quarter of the screen:
 *
 * - every fight: what the stretcher is doing (on, waiting for the hero, on the ground with who to get up, there), and
 *   whether the hero's heals reach Tessa from where it stands (they do beside her).
 * - the slope (the test): the limpets stuck to the stretcher (red while there are) and those on the hero.
 * - the chapel: where the lantern is (on the stretcher, or hung and the stretcher in the dark), the lamp over the door
 *   (dark, lit) and how many mirrors the beam goes by. Over the board, a mark on each hook and each mirror, and one on
 *   the stretcher while the lantern hangs (to bring it back); a tap sends the hero there, and a ring fills while it
 *   works it.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class CaminoHud {
  readonly panel: HTMLElement;
  private kind: 'street' | 'chapel' | 'slope' | 'ridge' | 'walk' | 'bridge' | 'col' | 'frost' | 'corral' | 'vigil' | 'nave' | 'lamps' | 'procession' | 'mourn';
  private v = new THREE.Vector3();
  private litterRow = h('div.bh-row.cm-litter');
  private litterText = h('span.al-wave');
  private mendRow = h('div.bh-row.cm-mend');
  private mendText = h('span.al-wave');
  private limpetRow = h('div.bh-row.al-waverow.cm-limpets');
  private limpetText = h('span.al-wave');
  private lanternRow = h('div.bh-row.cm-lantern');
  private lanternText = h('span.al-wave');
  private lampRow = h('div.bh-row.cm-lamp');
  private lampText = h('span.al-wave');
  private marks: Mark[] = [];
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.litter;
    this.kind = r?.kind ?? 'street';
    this.litterRow.append(h('span.bh-ico', { html: LITTER }), h('span.bh-label', {}, t('senda.camino.litter')), this.litterText);
    this.mendRow.append(h('span.bh-ico', { html: MEND }), this.mendText);
    // (Chapter 4's memory has no stretcher; its nave keeps only the limpets here: a phone's plate stays short.)
    // (Chapter 5's own plate says the rest: game/vigilia-hud.ts.)
    const rows: HTMLElement[] = this.kind === 'vigil' || this.kind === 'nave' || this.kind === 'lamps' || this.kind === 'procession' || this.kind === 'mourn' ? [] : [this.litterRow, this.mendRow];
    if (this.kind === 'slope' || this.kind === 'bridge' || this.kind === 'nave') {
      this.limpetRow.append(h('span.bh-ico', { html: LIMPET }), h('span.bh-label', {}, t('senda.camino.limpets')), this.limpetText);
      rows.push(this.limpetRow);
    }
    if (this.kind === 'chapel' && r?.mirrors) {
      this.lanternRow.append(h('span.bh-ico', { html: LANTERN }), h('span.bh-label', {}, t('senda.camino.lantern')), this.lanternText);
      this.lampRow.append(h('span.bh-ico', { html: LAMP }), h('span.bh-label', {}, t('senda.camino.lamp')), this.lampText);
      rows.push(this.lanternRow, this.lampRow);
      const M = r.mirrors;
      M.hooks.forEach((hk, i) => this.addMark(i, hexToWorld(hk.q, hk.r), HOOK, t('senda.camino.hook'), t('senda.camino.hookHint')));
      M.mirrors.forEach((m, i) => this.addMark(MIRROR_MARK + i, hexToWorld(m.q, m.r), MIRROR, `${t('senda.camino.mirror')} ${i + 1}`, t('senda.camino.mirrorHint')));
      const w0 = r.way[0] as { x: number; y: number };
      this.addMark(LITTER_BACK, w0, LANTERN, t('senda.camino.back'), t('senda.camino.backHint'));
    }
    this.panel = h(`div.bh.cm-panel.cm-${this.kind}`, {}, ...rows);
  }

  private addMark(at: number, p: { x: number; y: number }, ico: string, name: string, hint: string): void {
    const icon = h('span.cm-ico', { html: ico });
    const el = h(
      `button.al-stone.cm-mark${at === LITTER_BACK ? '.back' : at < MIRROR_MARK ? '.hook' : '.mirror'}`,
      { type: 'button', 'data-test': `camino-mark-${at}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      icon,
      h('b.mn-name.cm-name', {}, name),
    ) as HTMLButtonElement;
    this.board.append(el);
    this.marks.push({ el, at, point: simToWorld(p.x, p.y, new THREE.Vector3(), at < MIRROR_MARK ? 2.3 : at === LITTER_BACK ? 1.6 : 2.4), ico: icon });
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.litter;
    const w = s.senda?.litter;
    if (!r || !w) return;
    const hero = s.heroes.find((x) => x.downTicks === 0);
    const tessa = s.senda?.npcs.find((n) => n.id === 'tessa');
    const down = s.senda?.npcs.find((n) => (n.id === 'piet' || n.id === 'ludo') && n.hp <= 0);
    const near = !!hero && Math.hypot(hero.x - w.x, hero.y - w.y) <= r.near;
    const lifting = down ? (w.lift[down.id === 'piet' ? 0 : 1] ?? 0) > 0 : false;
    const name = down ? t(`senda.cleric.npc.${down.id}`) : '';
    // (Chapter 3: the terrace and the corral, the stretcher set down where it rests.)
    const rests = r.kind === 'chapel' || r.kind === 'frost' || r.kind === 'corral' || r.kind === 'nave';
    const text =
      rests
        ? down
          ? t(lifting ? 'senda.camino.lifting' : 'senda.camino.down', { name })
          : t(r.kind === 'corral' ? 'senda.camino.restsCorral' : r.kind === 'frost' ? 'senda.camino.restsFrost' : r.kind === 'nave' ? 'senda.camino.restsAltar' : 'senda.camino.rests')
        : w.done >= 0
          ? t('senda.camino.there')
          : down
            ? t(lifting ? 'senda.camino.lifting' : 'senda.camino.down', { name })
            : // (Chapter 2: waiting in a chapel, told to wait.)
              w.pas && r.ridge && w.pas.wait >= 0
              ? t('senda.camino.sheltered')
              : w.pas && r.span && !w.pas.go
                ? t('senda.camino.stops')
                : w.moving
              ? t('senda.camino.goes')
              : near
                ? t('senda.camino.goes')
                : t('senda.camino.waits');
    if (this.litterText.textContent !== text) this.litterText.textContent = text;
    this.litterRow.classList.toggle('warn', !!down || (!near && w.done < 0 && !rests) || (!!w.pas && !!r.span && !w.pas.go && w.done < 0));
    const reach = !!hero && !!tessa && Math.hypot(hero.x - tessa.x, hero.y - tessa.y) <= r.mendReach;
    const mt = t(reach ? 'senda.camino.mend' : 'senda.camino.far');
    if (this.mendText.textContent !== mt) this.mendText.textContent = mt;
    this.mendRow.classList.toggle('good', reach);
    this.mendRow.classList.toggle('dim', !reach);
    if (this.kind === 'slope' || this.kind === 'bridge' || this.kind === 'nave') {
      let on = 0;
      let mine = 0;
      for (const l of w.limpets) {
        if (l.on === 1) on++;
        else if (l.on === 2 || l.on === 3) mine++;
      }
      const lt = on || mine ? [on ? t('senda.camino.onLitter', { n: on }) : '', mine ? t('senda.camino.onHero', { n: mine }) : ''].filter(Boolean).join(' · ') : t('senda.camino.none');
      if (this.limpetText.textContent !== lt) this.limpetText.textContent = lt;
      this.limpetRow.classList.toggle('warn', on > 0);
    }
    if (this.kind === 'chapel') {
      const lt = w.lantern < 0 ? t('senda.camino.lanternOn') : t('senda.camino.lanternHung');
      if (this.lanternText.textContent !== lt) this.lanternText.textContent = lt;
      this.lanternRow.classList.toggle('warn', w.lantern >= 0);
      const lit = w.lit >= 0;
      const lp = lit ? t('senda.camino.lampLit') : w.lantern >= 0 ? `${t('senda.camino.lampDark')} · ${t('senda.camino.beamTo', { n: w.hits })}` : t('senda.camino.lampDark');
      if (this.lampText.textContent !== lp) this.lampText.textContent = lp;
      this.lampRow.classList.toggle('good', lit);
      const key = `${w.lantern}|${w.lit}|${w.job}`;
      if (key !== this.key) {
        this.key = key;
        for (const m of this.marks) {
          m.el.classList.toggle('gone', lit || (m.at === LITTER_BACK && w.lantern < 0));
          m.el.classList.toggle('has', m.at < MIRROR_MARK && w.lantern === m.at);
          remeasureMark(m.el);
        }
      }
      for (const m of this.marks) {
        const sent = w.job === m.at;
        m.el.classList.toggle('sent', sent);
        m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.mirrors?.stand ?? 12)).toFixed(2) : '0');
        m.el.disabled = s.result !== 'running';
      }
      // The lantern-back mark goes with the stretcher.
      const back = this.marks.find((m) => m.at === LITTER_BACK);
      if (back) simToWorld(w.x, w.y, back.point, 1.5);
    }
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
